// MACHINERY V2 (Stroje) — shared atoms for the four profile tab bodies (M2.5).
//
// Spec: MACHINERY_V2_IMPLEMENTATION_TICKET.md §8 M2.5. Reference impls:
// design_handoff_machinery_v3/machinery-profile-tabs.jsx (PtSearch,
// PtFilterChip, PtSortGlyph, the panel + dashed-empty idiom) and
// machinery-profile-parts.jsx (the same panel, PcSelect). Artboards 1000-1011.
//
// ONE COPY OF THE TAB CHROME. The four tabs draw the same panel, the same
// dashed empty state, the same mono count line and the same search box. The
// designer file has that duplication because it is a prototype; here it would
// be four places to fix a padding. Everything in this file is purely
// presentational — no data, no dispatch, no permission logic.
//
// SKELETONS ARE STATIC. Artboards 1002 and 1010 draw plain tinted bars with a
// descending opacity ramp and NO shimmer. That is also why nothing here needs a
// keyframe: machinery-v2-tokens.scss is at 288 of its 300 lines and the tabs
// add nothing to it.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

/** The white panel every tab list sits in (artboards 1000/1003/1006/1009). */
export const TabCard: React.FC<{
  children: React.ReactNode;
  /** "visible" when a kebab menu must escape the panel; "hidden" otherwise. */
  overflow?: "hidden" | "visible";
}> = ({ children, overflow = "hidden" }) => (
  <div
    style={{
      background: "var(--card)",
      border: "1px solid var(--border)",
      borderRadius: 10,
      boxShadow: "var(--shadow)",
      overflow,
      minWidth: 0,
    }}
  >
    {children}
  </div>
);

/** Tab header line: a sentence on the left, a mono count on the right. */
export const TabIntro: React.FC<{ text?: string; count?: string }> = ({ text, count }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 8,
      minWidth: 0,
      flexWrap: "wrap",
    }}
  >
    {text && <div style={{ fontSize: 13, color: "var(--muted)", minWidth: 0 }}>{text}</div>}
    <span style={{ flex: 1 }} />
    {count && (
      <span
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: 10,
          color: "var(--faint)",
          letterSpacing: "0.04em",
          whiteSpace: "nowrap",
        }}
      >
        {count}
      </span>
    )}
  </div>
);

/**
 * The dashed empty / no-results panel.
 *
 * The two cases differ by DESIGN, not by accident (artboards 1002, 1005, 1010,
 * 1011): "nothing exists yet" offers the create CTA, "nothing matches your
 * filter" offers a way back to everything and NO create button — putting one
 * there invites the user to create a record in order to escape a filter.
 */
export const TabEmpty: React.FC<{
  title: string;
  hint?: string;
  ctaLabel?: string;
  onCta?: () => void;
  clearLabel?: string;
  onClear?: () => void;
}> = ({ title, hint, ctaLabel, onCta, clearLabel, onClear }) => (
  <div
    style={{
      border: "1px dashed var(--border)",
      borderRadius: 10,
      background: "var(--card)",
      padding: "26px 16px",
      textAlign: "center",
      minWidth: 0,
    }}
  >
    <div style={{ fontSize: 13.5, fontWeight: 600, color: "var(--ink)" }}>{title}</div>
    {(hint || clearLabel) && (
      <div style={{ fontSize: 12.5, color: "var(--muted)", marginTop: 3 }}>
        {hint}
        {clearLabel && onClear && (
          <>
            {hint ? " " : null}
            <button
              type="button"
              onClick={onClear}
              style={{
                border: 0,
                background: "transparent",
                padding: 0,
                font: "inherit",
                color: "var(--mod-accent)",
                cursor: "pointer",
                textDecoration: "underline",
              }}
            >
              {clearLabel}
            </button>
          </>
        )}
      </div>
    )}
    {ctaLabel && onCta && (
      <button
        type="button"
        className="btn btn-mod"
        onClick={onCta}
        style={{ marginTop: 12, whiteSpace: "nowrap" }}
      >
        <span aria-hidden style={{ fontSize: 15, lineHeight: 1, marginRight: 2 }}>
          +
        </span>
        {ctaLabel}
      </button>
    )}
  </div>
);

/** One skeleton bar. `dim` drives the artboard descending-opacity ramp. */
export const SkeletonBar: React.FC<{
  height?: number;
  width?: string;
  dim?: number;
  radius?: number;
}> = ({ height = 40, width = "100%", dim = 0, radius = 5 }) => (
  <div
    aria-hidden
    style={{
      height,
      width,
      borderRadius: radius,
      background: "var(--border-soft)",
      opacity: Math.max(0.35, 1 - dim * 0.15),
    }}
  />
);

const SearchGlyph: React.FC = () => (
  <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden>
    <circle cx="5.5" cy="5.5" r="4" stroke="currentColor" />
    <line x1="8.5" y1="8.5" x2="11.5" y2="11.5" stroke="currentColor" strokeLinecap="round" />
  </svg>
);

/** Controlled search box — the FleetToolbar idiom, sized for a tab body. */
export const TabSearch: React.FC<{
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  clearLabel: string;
  maxWidth?: number;
}> = ({ value, onChange, placeholder, clearLabel, maxWidth = 320 }) => (
  <div
    style={{
      height: 32,
      padding: "0 10px",
      background: "var(--card)",
      border: "1px solid var(--border)",
      borderRadius: 7,
      display: "flex",
      alignItems: "center",
      gap: 8,
      color: "var(--faint)",
      flex: "1 1 200px",
      minWidth: 160,
      maxWidth,
    }}
  >
    <SearchGlyph />
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      aria-label={placeholder}
      style={{
        flex: 1,
        minWidth: 0,
        border: 0,
        outline: "none",
        background: "transparent",
        fontFamily: "inherit",
        fontSize: 12.5,
        color: "var(--ink)",
      }}
    />
    {value && (
      <button
        type="button"
        onClick={() => onChange("")}
        aria-label={clearLabel}
        style={{
          border: 0,
          background: "transparent",
          color: "var(--muted)",
          cursor: "pointer",
          padding: 0,
          fontSize: 13,
          lineHeight: 1,
        }}
      >
        &times;
      </button>
    )}
  </div>
);

/** Two-triangle sort indicator; the lit half shows the active direction. */
export const SortGlyph: React.FC<{ dir?: "asc" | "desc" }> = ({ dir }) => (
  <svg viewBox="0 0 8 10" width="7" height="9" style={{ marginLeft: 4, flexShrink: 0 }} aria-hidden>
    <path d="M4 0.5 L7 3.5 L1 3.5 Z" fill={dir === "asc" ? "var(--mod-accent)" : "var(--border)"} />
    <path d="M4 9.5 L1 6.5 L7 6.5 Z" fill={dir === "desc" ? "var(--mod-accent)" : "var(--border)"} />
  </svg>
);

/** Shared style for an uppercase mono column header cell. */
export const headCellStyle: React.CSSProperties = {
  fontFamily: "var(--font-mono)",
  fontSize: 9,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  color: "var(--faint)",
  display: "inline-flex",
  alignItems: "center",
  minWidth: 0,
};

/** The stack of skeleton bars shown while a list loads (artboard 1002). */
export const SkeletonList: React.FC<{ rows?: number; height?: number }> = ({
  rows = 4,
  height = 40,
}) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 8, padding: 14 }}>
    {Array.from({ length: rows }, (_unused, i) => (
      <SkeletonBar key={i} height={height} dim={i} />
    ))}
  </div>
);
